import { UnifiedOverlay } from "./map";
import { MobilityReportByUser, RouteInfo } from "../types/types";
import {
  ArrowForMap,
  MapStartImage,
  MapEndImage,
  GreenEndMarkerForRunning,
  RedEndMarkerForRunning,
  OrangeEndMarkerForRunning,
} from "../assets/icons/icons";
import theme from "../theme";
import { randomInt } from "../utils/util";
import { getEventTypeKey } from "../utils/langUtils";
import { useI18n } from "../locales";

// 두 점 간의 각도를 계산하는 함수
const getAngle = (center: any, target: any) => {
  const dx = target.x - center.x;
  const dy = center.y - target.y;
  return ((-Math.atan2(dy, dx) * 180) / Math.PI + 450) % 360 | 0;
};

// 운행 상태에 따른 시작 마커 선택 함수
const getStartMarker = (events?: string[] | null, t?: any): JSX.Element | null => {
  if (!events) return <MapStartImage />;
  // 차량전복 이벤트 확인 (다국어 지원)
  const hasRollover = events.some((event) => {
    if (!t) return event === "차량전복";
    const eventKey = getEventTypeKey(event, t);
    return eventKey === "vehicleRolloverEvent" || event === "차량전복";
  });
  if (hasRollover)
    return <MapStartImage color={theme.color.red_500} />;
  return <MapStartImage color={theme.color.orange_500} />;
};

// 운행 상태에 따른 종료 마커 선택 함수
const getEndMarker = (
  status: string | null,
  events?: string[] | null,
  t?: any
): JSX.Element | null => {
  // 차량전복 이벤트 확인 (다국어 지원)
  const hasRollover = events?.some((event) => {
    if (!t) return event === "차량전복";
    const eventKey = getEventTypeKey(event, t);
    return eventKey === "vehicleRolloverEvent" || event === "차량전복";
  });
  
  // 운행종료 상태 확인 (다국어 지원)
  const isOperationEnd = status === "운행종료" || (t && status === t.control.operationEnd);
  const isOperationRunning = status === "운행중" || (t && status === t.control.operationRunning);
  
  if (isOperationEnd) {
    if (!events) return <MapEndImage />;
    if (hasRollover)
      return <MapEndImage color={theme.color.red_500} />;
    return <MapEndImage color={theme.color.orange_500} />;
  }
  if (isOperationRunning) {
    if (!events) return <GreenEndMarkerForRunning />;
    if (hasRollover) return <RedEndMarkerForRunning />;
    return <OrangeEndMarkerForRunning />;
  }
  return null;
};

// 마커 생성 함수
const createMarker = (
  position: { lat: number; lng: number },
  zIndex: number,
  children: JSX.Element | null
) => {
  if (children) {
    return (
      <UnifiedOverlay
        key={randomInt()}
        clickable={false}
        position={position}
        zIndex={zIndex}
      >
        {children}
      </UnifiedOverlay>
    );
  }
  return null;
};

const CustomMarkers = (
  routeData?: RouteInfo[],
  selectedData?: MobilityReportByUser | null
) => {
  const { t } = useI18n();
  const result: any[] = [];

  // 경로가 없는 경우
  if ((!routeData || routeData.length === 0) && selectedData) {
    if (selectedData.start_latitude && selectedData.start_longitude) {
      result.push(
        createMarker(
          {
            lat: selectedData.start_latitude,
            lng: selectedData.start_longitude,
          },
          theme.zindex.startMarker,
          <div style={{ transform: 'translate(-20px, -20px)' }}>
            {getStartMarker(selectedData.events, t)}
          </div>
        )
      );
      // result.push(
      //   createMarker(
      //     {
      //       lat: selectedData.start_latitude,
      //       lng: selectedData.start_longitude,
      //     },
      //     theme.zindex.endMarker,
      //     getEndMarker(selectedData.status, selectedData.events)
      //   )
      // );
    }
    if (
      selectedData.end_latitude &&
      selectedData.end_longitude
      // && selectedData.status === "운행종료"
    ) {
      const endMarker = getEndMarker(selectedData.status, selectedData.events, t);
      result.push(
        createMarker(
          {
            lat: selectedData.end_latitude,
            lng: selectedData.end_longitude,
          },
          theme.zindex.endMarker,
          <div style={{ transform: 'translate(-20px, -20px)' }}>
            {endMarker}
          </div>
        )
      );
    }
    return result;
  }

  // 경로가 있는 경우
  if (selectedData && routeData && routeData.length > 0) {
    const [start] = routeData;
    const end = routeData[routeData.length - 1];

    // 시작 마커
    result.push(
      createMarker(
        { lat: start.latitude, lng: start.longitude },
        theme.zindex.startMarker,
        <div style={{ transform: 'translate(-20px, -20px)' }}>
          {getStartMarker(selectedData.events, t)}
        </div>
      )
    );

    // 종료 마커
    const endMarker = getEndMarker(
      selectedData.status,
      selectedData.events,
      t
    );
    if (endMarker) {
      result.push(
        createMarker(
          { lat: end.latitude, lng: end.longitude },
          theme.zindex.endMarker,
          <div style={{ transform: 'translate(-20px, -20px)' }}>
            {endMarker}
          </div>
        )
      );
    }

    // 중간 구간 화살표 마커
    for (let i = 1; i < routeData.length - 1; i++) {
      const origin = {
        x: routeData[i - 1].longitude, // x = 경도 (longitude)
        y: routeData[i - 1].latitude,  // y = 위도 (latitude)
      };
      const target = {
        x: routeData[i].longitude,     // x = 경도 (longitude)
        y: routeData[i].latitude,      // y = 위도 (latitude)
      };
      const angle = getAngle(origin, target);

      // "정상" 이벤트 확인 (다국어 지원)
      const eventKey = getEventTypeKey(routeData[i].event_type, t);
      const isNormal = eventKey === "normal" || routeData[i].event_type === "정상";
      if (isNormal) {
        result.push(
          createMarker(
            { lat: routeData[i].latitude, lng: routeData[i].longitude },
            theme.zindex.arrowMarker,
            <div style={{ transform: 'translate(-7.5px, -7.5px)' }}>
              <ArrowForMap rotate={angle} />
            </div>
          )
        );
      }
    }
    return result;
  }
  return result;
};

export default CustomMarkers;
